import { ChevronDownIcon } from 'lucide-react-native';
import { type ReactNode, useState } from 'react';
import { Pressable, ScrollView } from 'react-native';
import { Popover, View, XStack, YStack } from 'tamagui';

import { FruitfulCustomIcon } from '../icons/FruitfulCustomIcon';
import { Icon } from '../icons/Icon';
import { Shadows } from '../Shadows';
import { BodyMediumEmphasized, BodyMediumRegular } from '../text/Typography';

export interface IconSelectDropdownOption {
    value: string;
    /** Name of a FruitfulCustomIcon. Ignored when `iconElement` is provided. */
    icon?: string;
    /** Custom icon to render instead of `icon` (e.g. an institution logo). */
    iconElement?: ReactNode;
    label?: string;
    color?: string;
    backgroundColor?: string;
}

export interface IconSelectDropdownProps {
    options: IconSelectDropdownOption[];
    value: string | undefined;
    onValueChange: (value: string, index: number) => void;
    placeholder?: string;
    disabled?: boolean;
    /**
     * 'inline' (default) renders the trigger as plain text with a chevron.
     * 'field' renders it as a bordered, input-style row that fills its container.
     */
    variant?: 'inline' | 'field';
}

const OptionIcon = ({ option }: { option: IconSelectDropdownOption }) => {
    if (option.iconElement != null) {
        return <>{option.iconElement}</>;
    }
    if (option.icon == null) {
        return null;
    }
    return (
        <View
            backgroundColor={option.backgroundColor ?? 'transparent'}
            borderRadius={8}
            padding={(option.backgroundColor ?? '') !== '' ? 4 : 0}
        >
            <FruitfulCustomIcon
                icon={option.icon}
                color={option.color ?? '$gray1200'}
                size="xs"
            />
        </View>
    );
};

export const IconSelectDropdown = ({
    options,
    value,
    onValueChange,
    placeholder = 'Select',
    disabled = false,
    variant = 'inline',
}: IconSelectDropdownProps) => {
    const isField = variant === 'field';
    const [open, setOpen] = useState(false);
    const selectedOption =
        value !== undefined && value !== ''
            ? options.find((o) => o.value === value)
            : undefined;
    const displayLabel =
        selectedOption?.label ?? selectedOption?.value ?? placeholder;
    const triggerColor = open ? '$gray1200' : '$gray1000';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <Pressable
                    disabled={disabled}
                    style={isField ? { width: '100%' } : { minWidth: 306 }}
                >
                    {isField ? (
                        <XStack
                            backgroundColor="$gray200"
                            borderWidth={1}
                            borderColor="$gray600"
                            borderRadius="$4"
                            height={56}
                            paddingHorizontal={16}
                            gap={8}
                            alignItems="center"
                            cursor={disabled ? 'default' : 'pointer'}
                        >
                            {selectedOption != null && (
                                <OptionIcon option={selectedOption} />
                            )}
                            <BodyMediumRegular
                                flex={1}
                                color={
                                    selectedOption != null
                                        ? '$gray1200'
                                        : '$gray1000'
                                }
                            >
                                {displayLabel}
                            </BodyMediumRegular>
                            {!disabled && (
                                <Icon
                                    icon={ChevronDownIcon}
                                    size={16}
                                    color="$gray1100"
                                />
                            )}
                        </XStack>
                    ) : (
                        <XStack
                            alignItems="center"
                            gap={4}
                            cursor={disabled ? 'default' : 'pointer'}
                        >
                            <BodyMediumRegular color={triggerColor}>
                                {displayLabel}
                            </BodyMediumRegular>
                            {!disabled && (
                                <Icon
                                    icon={ChevronDownIcon}
                                    size={16}
                                    color={triggerColor}
                                />
                            )}
                        </XStack>
                    )}
                </Pressable>
            </Popover.Trigger>
            <Popover.Content
                placement={isField ? 'bottom-start' : undefined}
                borderWidth={0}
                padding={8}
                borderRadius={16}
                style={[Shadows.LARGE, { minWidth: 306 }]}
            >
                <ScrollView
                    style={{ maxHeight: 320, width: '100%' }}
                    showsVerticalScrollIndicator={false}
                >
                    <YStack gap={4}>
                        {options.map((option, index) => {
                            const isSelected = option.value === value;
                            return (
                                <Popover.Close key={option.value} asChild>
                                    <Pressable
                                        onPress={() => {
                                            onValueChange(option.value, index);
                                        }}
                                    >
                                        <View
                                            backgroundColor={
                                                isSelected
                                                    ? '$gray200'
                                                    : '$white'
                                            }
                                            padding={12}
                                            borderRadius={12}
                                            hoverStyle={
                                                !isSelected
                                                    ? {
                                                          backgroundColor:
                                                              '$gray100',
                                                      }
                                                    : undefined
                                            }
                                        >
                                            <XStack
                                                alignItems="center"
                                                gap={12}
                                            >
                                                <OptionIcon option={option} />
                                                <BodyMediumEmphasized color="$gray1200">
                                                    {option.label ??
                                                        option.value}
                                                </BodyMediumEmphasized>
                                            </XStack>
                                        </View>
                                    </Pressable>
                                </Popover.Close>
                            );
                        })}
                    </YStack>
                </ScrollView>
            </Popover.Content>
        </Popover>
    );
};
